import { camelCase, get, mapValues } from 'lodash';
import ReactGA from 'react-ga';
import type { Primitive } from 'utility-types';

import { GADimension } from '@swf/core/enums';
import type { Entity } from '@swf/generated/analytics/entities';
import { widget } from '@swf/generated/analytics/entities';

import type { TrackingContextValue } from '../providers/TrackingContext';

export const GAEvent = (
  params: Omit<Parameters<typeof ReactGA.event>[0], 'category'>,
  { category, widget }: TrackingContextValue,
  dimensions?: Record<string, Primitive | string[]>
) => {
  const allDimensions = { ...dimensions, [GADimension.WidgetName]: widget?.widgetName };
  ReactGA.set(allDimensions);
  ReactGA.event({ ...params, category });
  ReactGA.set(mapValues(allDimensions, () => null));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const memoize = <T extends (...args: any[]) => void>(func: T, path: string) => {
  let cache: Primitive;
  return (...args: Parameters<T>) => {
    const newCache = get(args, path);
    if (newCache !== cache) {
      cache = newCache;
      func(...args);
    }
  };
};

const widgetEntityExists = (entities: Entity[] = [], widgetEntity: Entity) =>
  entities.find(({ schema }) => schema === widgetEntity.schema);

export const getEntities = (context: Omit<TrackingContextValue, 'category'> = {}) => {
  const entities: Entity[] = [];
  if (context.widget) {
    const widgetEntity = widget(context.widget);
    if (!widgetEntityExists(context.entities, widgetEntity)) {
      entities.push(widgetEntity);
    }
  }
  context.entities && entities.push(...context.entities);
  return entities;
};

export const getGTMData = (context: Omit<TrackingContextValue, 'category'> = {}) => ({
  widget: context.widget,
  ...context.entities?.reduce((memo, entity) => {
    let key = entity.schema.replace(/iglu:com.starsgroup\/(.*)\/jsonschema\/.*/, '$1');
    key = `${camelCase(key)}s`;
    memo[key] = memo[key] || [];
    memo[key].push(entity.data);
    return memo;
  }, {} as Record<string, Entity['data'][]>)
});
